import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Container, Row, Col } from "reactstrap";
import MainNavigation from "../components/MainNavigation";
import OrderedItems from "../components/OrderedItems";
import PricingSection from "../components/PricingSection";
import {
  addCartDetails,
  clearCartAction,
  clearCartDetails,
  getCheckoutQuestions,
  getTableData,
  updateDelivery,
} from "../store/actions/cartAction";
import { getRestaurantsAction } from "../store/actions/restaurantsAction";
import CheckoutQuestions from "../components/Cart/CheckoutQuestions";
import TableSelection from "../components/Cart/TableSection";
import AddressAutocomplete from "../components/Cart/AddressAutoComplete";
import PayButton from "../components/Cart/PayButton";
import Shipment from "../components/Shipment";
import HolidayNotice from "../components/RestaurantMenu/HolidayNotice";

const Cart = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [filteredTables, setFilteredTables] = useState([]);
  const toastShownRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { darkMode, color } = useSelector((state) => state.global);
  const { uid, user } = useSelector((state) => state.authUser);
  const {
    cartItems,
    checkoutQuestions,
    tipDetails,
    addressDetails,
    allTablesData,
    orderType,
    cartDetails,
  } = useSelector((state) => state.cart);

  const { restaurantsDetail, userTableInfo } = useSelector(
    (state) => state.restaurants
  );
  const allTables = Object.values(allTablesData?.allTables || {});
  const currentUserTableInfo = allTables.find(
    (table) => table.customerId === uid
  );
  const [addressInput, setAddressInput] = useState("");
  const [answers, setAnswers] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [distanceInMeters, setDistanceInMeters] = useState(0);

  const [price, setPrice] = useState(0);
  const [reservationWarningShown, setReservationWarningShown] = useState(false);
  const [tableNumber, setTableNumber] = useState("");
  const [finalPrice, setFinalPrice] = useState(0);
  const [appliedCharges, setAppliedCharges] = useState([]);
  const [loader, setLoader] = useState(false);
  const [cartData, setCartData] = useState([]);
  const restaurantID = cartDetails?.restaurantId;
  const [isHoliday, setIsHoliday] = useState({
    isHoliday: false,
    title: "",
    storeTiming: false,
    type: "",
  });
  const currency = cartItems?.[0]?.currency;

  const shipment = {
    storeName: restaurantsDetail?.name || "Sample Restaurant",
    shipmentFrom: "From Store",
  };

  const handleChange = (event, questionId) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        answer: event.target.value,
        customerId: uid,
        questionId,
        customerName: user?.name,
        customerAge: user?.age,
        customerGender: user?.gender,
      },
    }));
  };

  const onPlaceChanged = useCallback(
    (place) => {
      if (place?.geometry?.location) {
        const { location } = place.geometry;
        setAddressInput(place.formatted_address);
        dispatch(
          updateDelivery({
            lat: location.lat(),
            lng: location.lng(),
            address: place.formatted_address,
          })
        );
      }
    },
    [dispatch]
  );

  const handlePayButton = (e) => {
    e.preventDefault();
    if (isHoliday?.isHoliday || isHoliday?.storeTiming) {
      const typeLabel =
        isHoliday?.type === "dinein"
          ? "Dine-in"
          : isHoliday?.type === "delivery"
          ? "Delivery"
          : "Takeaway";

      const typeIcon =
        isHoliday?.type === "dinein"
          ? "🍽️"
          : isHoliday?.type === "delivery"
          ? "🚚"
          : "🛍️";

      toast.warning(
        isHoliday?.isHoliday
          ? `📅 Attention: The ${restaurantsDetail?.name} store is closed today due to ${isHoliday?.title}. Please check back tomorrow.`
          : `${typeIcon} Notice: ${typeLabel} service is currently unavailable as operating hours have ended. Please select a different option or check back tomorrow.`,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          style: {
            backgroundColor: "#fff3cd",
            color: "#856404",
            fontWeight: "500",
            fontSize: "15px",
          },
        }
      );

      if (isHoliday?.storeTiming) {
        history.push("/order-options");
        dispatch(clearCartAction());
      } else if (isHoliday?.isHoliday) {
        history.push("/restaurants");
        dispatch(clearCartAction());
        dispatch(clearCartDetails());
      }
      return;
    }
    const hasEmptyMandatoryField = checkoutQuestions?.some(
      (question) => question.isMandatory && !answers[question.id]?.answer
    );

    if (hasEmptyMandatoryField)
      return toast.warning("Please fill all mandatory fields.");

    if (!isMountedRef.current) return;
    setLoader(true);

    try {
      const existingCart = cartDetails || {};
      const updatedCart = {
        ...existingCart,
        tableNumber: tableNumber || "",
        deliveryAddress: addressDetails || "",
        totalAmount,
        totalPriceItem: cartData.reduce(
          (acc, item) => acc + item.totalPrice,
          0
        ),
        finalPrice,
        appliedCharges,
        answers,
        currency,
        restaurantID: cartDetails?.restaurantId,
        timer: allTablesData?.timer,
      };
      setTimeout(() => {
        if (isMountedRef.current) {
          dispatch(addCartDetails(updatedCart));
          history.push("/payment");
        }
      }, 1000);
    } finally {
      setTimeout(() => {
        if (isMountedRef.current) {
          setLoader(false);
        }
      }, 1000);
    }
  };

  const calculateFinalPriceAndCharges = useCallback(() => {
    let basePrice =
      Number(cartData?.reduce((acc, item) => acc + item?.totalPrice, 0)) || 0;
    let finalPrice = basePrice;
    let appliedCharges = [];

    restaurantsDetail?.additionalCharges?.forEach((charge) => {
      if (charge.isActive) {
        const fee = Number(charge.fee) || 0;
        let addedAmount =
          charge.type === "fixed" ? fee : (finalPrice * fee) / 100;

        finalPrice += addedAmount;
        appliedCharges.push({
          name: charge.name || "Additional Charge",
          type: charge.type,
          fee,
          addedAmount: addedAmount.toFixed(2),
        });
      }
    });

    return { finalPrice, appliedCharges };
  }, [cartData, restaurantsDetail]);

  useEffect(() => {
    const { finalPrice: calculatedPrice, appliedCharges: charges } =
      calculateFinalPriceAndCharges();
    setFinalPrice(calculatedPrice);
    setAppliedCharges(charges);
  }, [cartData, restaurantsDetail, calculateFinalPriceAndCharges]);

  useEffect(() => {
    if (currentUserTableInfo?.tableNo) {
      setTableNumber(currentUserTableInfo.tableNo);
    } else {
      setTableNumber("");
    }
  }, [currentUserTableInfo]);

  useEffect(() => {
    if (cartItems?.length == 0) {
      toast.warning("Please add Items in cart");
      history.push(`/restaurant/${cartDetails?.restaurantId}`);
      return;
    }
    setCartData(
      cartItems?.map((obj) => ({
        ...obj,
        images: obj.images,
        totalPrice: obj.totalPrice,
      }))
    );

    const existingCart = cartDetails || {};
    const updatedCart = {
      ...existingCart,
      order: cartItems,
    };
    dispatch(addCartDetails(updatedCart));
  }, [cartItems]);

  useEffect(() => {
    const totalPrice = cartData?.reduce(
      (acc, item) => acc + item.totalPrice,
      0
    );
    setPrice(totalPrice);
    setTotalAmount(totalPrice);
  }, [cartData]);

  useEffect(() => {
    if (tipDetails) {
      const tipAmount =
        tipDetails.tipType === "fixed"
          ? tipDetails.tip
          : (tipDetails.tip / 100) * price;
      setTotalAmount(price + tipAmount);
    }
  }, [price, tipDetails]);

  useEffect(() => {
    if (!restaurantID) {
      toast.warning("Restaurant is not added .. Please select Restaurant");
      history.push("/restaurants");
      dispatch(clearCartAction());
      dispatch(clearCartDetails());
      return;
    }
    // if (!cartDetails?.orderType) {
    //   toast.warning("Order-type is not added .. Please select Order-Type");
    //   history.push("/order-options");
    //   return;
    // }

    if (restaurantID) {
      dispatch(getRestaurantsAction(restaurantID));
      dispatch(getTableData(restaurantID));
      dispatch(getCheckoutQuestions(restaurantID));
    }
  }, [dispatch, restaurantID, cartDetails]);

  useEffect(() => {
    const getAvailableTables = () => {
      if (cartDetails?.orderType !== "dinein") return [];

      const currentTime = new Date().getTime();
      const fiveMinutes = 5 * 60 * 1000;
      const allTables = Object.values(allTablesData?.allTables || {});

      const availableTables = allTables.filter((table) => {
        const isUnoccupied = !table.customerId && !table.expireAt && !table.occupiedAt; 

        const isOccupiedByCurrentUser =
          table.customerId === uid &&
          cartDetails?.restaurantId === restaurantsDetail?.id;

        let isExpired = false;
        if (table.expireAt?.seconds) {
          const expireAtMs =
            table.expireAt.seconds * 1000 +
            Math.floor(table.expireAt.nanoseconds / 1e6);
          isExpired = currentTime - expireAtMs > fiveMinutes;
        }

        return isUnoccupied || isExpired || isOccupiedByCurrentUser;
      });

      const noTableForThisUser = !allTables.some(
        (table) =>
          table.customerId === uid &&
          cartDetails?.restaurantId === restaurantsDetail?.id
      );

      if (
        availableTables?.length === 0 &&
        noTableForThisUser &&
        cartDetails?.orderType == "dinein" &&
        !toastShownRef.current
      ) {
        toast.warning(
          "🚫 All tables are currently reserved. Please choose a different ordering method."
        );
        toastShownRef.current = true;
      }

      return availableTables.sort((a, b) => a.tableNo - b.tableNo);
    };

    const tables = getAvailableTables();
    setFilteredTables(tables);
  }, [allTablesData, uid, cartDetails, restaurantsDetail]);

  useEffect(() => {
    if (
      !reservationWarningShown &&
      cartDetails?.orderType === "dinein" &&
      userTableInfo &&
      userTableInfo?.restaurantId !== restaurantsDetail?.id
    ) {
      toast.warning(
        `⚠️ You already occupied a table at ${" "} ${
          userTableInfo?.restaurantName || "restaurant"
        }. You can't occupy a table at another restaurant until the current reservation ends.`
      );
      setReservationWarningShown(true);
    }
  }, []);

  return (
    <div className="cart" style={{ minHeight: "100vh" }}>
      {filteredTables?.length === 0 &&
        currentUserTableInfo?.customerId !== uid &&
        cartDetails?.orderType == "dinein" && (
          <div className="extend-bar-container position-relative w-100 py-1">
            <div className="extend-bar-outer">
              <div
                className="extend-bar-inner"
                style={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: darkMode ? "#fff" : "#212121",
                }}
              >
                ⚠️ Currently no table is available. Please choose other ordering
                option
                <Link
                  to="/order-options"
                  className="ms-3 text-decoration-underline"
                  style={{
                    color,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  ⏳ Choose Other Option
                </Link>
              </div>
            </div>
          </div>
        )}
      {reservationWarningShown && cartDetails?.orderType == "dinein" && (
        <div className="extend-bar-container position-relative w-100 py-1">
          <div className="extend-bar-outer">
            <div
              className="extend-bar-inner"
              style={{
                fontSize: "15px",
                fontWeight: 600,
                color: darkMode ? "#fff" : "#212121",
              }}
            >
              ⚠️ You already occupied a table at{" "}
              {userTableInfo?.restaurantName || "restaurant"}. You can't occupy
              a table at another restaurant until the current reservation ends.`
              <Link
                to="/restaurants"
                className="ms-3 text-decoration-underline"
                style={{
                  color,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                ⏳ Go to Restaurant Options
              </Link>
            </div>
          </div>
        </div>
      )}

      <Container>
        {cartItems?.length >= 1 ? (
          <>
            <HolidayNotice
              restaurant={restaurantsDetail}
              setIsHoliday={setIsHoliday}
            />
            <MainNavigation title={"Cart"} />
            <Shipment data={shipment} color={color} />
            <OrderedItems data={cartData} title={"Order Details"} />

            <PricingSection
              data={{
                price: cartData.reduce((acc, item) => acc + item.totalPrice, 0),
                currency,
                total: totalAmount,
                finalPrice,
                basePrice: totalAmount,
                appliedCharges,
              }}
            />

            {uid && (
              <>
                <Row className="my-4 justify-content-center">
                  <Col md={6} className="d-flex flex-column gap-4">
                    <CheckoutQuestions
                      questions={checkoutQuestions}
                      answers={answers}
                      handleChange={handleChange}
                      darkMode={darkMode}
                    />
                    {orderType === "dinein" &&
                      (!currentUserTableInfo ||
                        currentUserTableInfo?.customerId == uid) && (
                        <div className="text-start">
                          <TableSelection
                            tables={filteredTables}
                            tableNumber={tableNumber}
                            setTableNumber={setTableNumber}
                            darkMode={darkMode}
                            color={color}
                            uid={uid}
                            userTableInfo={currentUserTableInfo}
                          />
                        </div>
                      )}
                  </Col>
                </Row>

                <Row className="my-4 justify-content-center">
                  <Col md={12} className="text-center">
                    {orderType === "delivery" && (
                      <AddressAutocomplete
                        addressInput={addressInput}
                        setAddressInput={setAddressInput}
                        onPlaceChanged={onPlaceChanged}
                        darkMode={darkMode}
                        setDistanceInMeters={setDistanceInMeters}
                      />
                    )}
                  </Col>
                </Row>
              </>
            )}

            <Row className="my-4 justify-content-center">
              <Col md={5} className="text-center">
                <PayButton
                  disabled={
                    (uid &&
                      (totalAmount === 0 ||
                        cartItems?.length === 0 ||
                        checkoutQuestions?.some(
                          (question) =>
                            question.isMandatory &&
                            !answers[question.id]?.answer
                        ) ||
                        !orderType ||
                        (orderType === "dinein" && !tableNumber) ||
                        (orderType === "delivery" && !addressInput) ||
                        finalPrice > 10000 ||
                        (currentUserTableInfo &&
                          currentUserTableInfo?.restaurantId !==
                            restaurantsDetail?.id))) ||
                    reservationWarningShown ||
                    distanceInMeters > 100
                  }
                  reservationWarningShown={reservationWarningShown}
                  currency={currency}
                  userTableInfo={userTableInfo}
                  finalPrice={finalPrice}
                  color={color}
                  tableNumber={tableNumber}
                  handlePayButton={handlePayButton}
                  loading={loader}
                  distanceInMeters={distanceInMeters}
                />
              </Col>
            </Row>
          </>
        ) : (
          <>
            <MainNavigation title={""} />
            <p
              className={`text-center py-5 mb-0 fw-bold responsive-text ${
                darkMode ? "normal-heading-dark" : "normal-heading"
              }`}
            >
              Cart is empty add an item to proceed
            </p>
          </>
        )}
      </Container>
    </div>
  );
};

export default Cart;
